import { useQuery } from '@apollo/client';
import { EWorldMapSetType } from '__generated__/globalTypes';
import { SelectMultipleValues } from 'elements/minute/sidebar/sidebarComponents/atomic/SelectMultipleValues';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSectionRow } from 'elements/minute/sidebar/sidebarSection/SidebarSectionRow';
import { WorldMapSets, WorldMapSetsVariables } from 'graphql/query/__generated__/WorldMapSets';
import { WORLD_MAP_SETS_QUERY } from 'graphql/query/worldQuery';
import { useAddMarkerToSetMutation } from 'hooks/marker/useAddMarkerToSetMutation';
import { useRemoveMarkerFromSetMutation } from 'hooks/marker/useRemoveMarkerFromSetMutation';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { sortBy } from 'lodash';
import { useContext } from 'react';
import { IWorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    marker: IWorldMarker;
}

export const MarkerFloorSection = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const worldMapId = ctxEdit.map.worldMapId;
    const markerId = props.marker.id;
    const { getTokenForMapId } = useWorldMapLocalStorage();

    const floors = sortBy(
        ctxEdit.map?.worldMapSets?.filter((e) => e.type === EWorldMapSetType.FLOOR) ?? [],
        (e) => e.orderIndex
    );

    const mapSetQuery = useQuery<WorldMapSets, WorldMapSetsVariables>(WORLD_MAP_SETS_QUERY, {
        variables: { worldMapId, token: getTokenForMapId(worldMapId) },
        skip: !floors?.length
    });

    const mapSets = mapSetQuery.data?.worldMap?.worldMapSets ?? [];

    const [removeMarkerFromSet] = useRemoveMarkerFromSetMutation();
    const [addMarkerToSet] = useAddMarkerToSetMutation();

    const selected = mapSets.filter((e) => e.markers.some((e) => e.worldMapMarkerId === markerId));

    if (!floors?.length) {
        return null;
    }

    return (
        <S_MarkerFloorSection className={props.className}>
            <SidebarSection title={Localization.FLOORS} icon={{ iconName: 'layer-group' }}>
                <SidebarContent withHover withTopPadding>
                    <SidebarSectionRow title={Localization.VISIBLE_IN}>
                        <S_SelectMultipleValues
                            options={floors.map((mapSet) => ({
                                label: mapSet.title,
                                value: mapSet.worldMapSetId
                            }))}
                            selectedOptions={selected.map((e) => ({
                                label: e.title,
                                value: e.worldMapSetId
                            }))}
                            onChange={(newOptions) => {
                                console.log(newOptions);
                                const toAdd = newOptions.filter((e) =>
                                    selected.every((selected) => selected.worldMapSetId !== e.value)
                                );
                                const toRemove = selected.filter((selected) =>
                                    newOptions.every((e) => e.value !== selected.worldMapSetId)
                                );

                                toAdd.forEach((add) => {
                                    const mapSet = mapSets.find(
                                        (e) => e.worldMapSetId === add.value
                                    );
                                    addMarkerToSet({
                                        mapSet,
                                        markerId,
                                        worldMapId
                                    });
                                });

                                toRemove.forEach((remove) => {
                                    removeMarkerFromSet({
                                        mapSet: remove,
                                        markerId,
                                        worldMapId
                                    });
                                });
                            }}
                        />
                    </SidebarSectionRow>
                </SidebarContent>
            </SidebarSection>
        </S_MarkerFloorSection>
    );
};

const S_MarkerFloorSection = styled.div``;

const S_SelectMultipleValues = styled(SelectMultipleValues)`
    width: 200px;
`;
